import React, { lazy } from 'react';
import GlobalStore from '../Store';
import Loadable from 'shared/src/components/Loadable';
import { protectedLoader } from 'shared/src/utils/protectedLoader';

import SettingsIcon from '@mui/icons-material/Settings';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import EditRoadOutlinedIcon from '@mui/icons-material/EditRoadOutlined';
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import LocalPoliceOutlinedIcon from '@mui/icons-material/LocalPoliceOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import ReportOutlinedIcon from '@mui/icons-material/ReportOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PolylineIcon from '@mui/icons-material/Polyline';
import ErrorPage from 'shared/src/components/ErrorPage';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import CircleNotificationsOutlinedIcon from '@mui/icons-material/CircleNotificationsOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import InstallationReportMobilePage from './deviceInstallation/InstallationReportMobilePage';
import FeatureGate from 'shared/src/components/FeatureGate';
import { useOrgType } from 'shared/src/hooks/useOrgType';
import DvirTemplate from '../components/images/dvirTemplate.svg';

const portalMenuItems = [
  { id: 'Dashboard', type: 'item', url: '/home', icon: DashboardOutlinedIcon }, // Don't change this item, it must be first, keep AS-IS!!!!
  { id: 'Map', type: 'parent-item', url: '/home/fleetmap', icon: MapOutlinedIcon },
  { id: 'Assets', type: 'sub-item', url: '/home/vehicles', icon: DevicesOtherOutlinedIcon },
  { id: 'Trips', type: 'sub-item', url: '/home/trips', icon: EditRoadOutlinedIcon },
  { id: 'Geofences', type: 'sub-item', url: '/home/geofences', icon: PolylineIcon },
  { id: 'Drivers', type: 'parent-item', url: '/home/drivers', icon: PermIdentityOutlinedIcon },
  { id: 'Driver Check-Ins', type: 'sub-item', url: '/home/driver-checkins', icon: HowToRegOutlinedIcon },
  { id: 'Maintenance', type: 'parent-item', url: '/home/maintenance', icon: HandymanOutlinedIcon },
  { id: 'Fault Codes', type: 'sub-item', url: '/home/faultcodes', icon: HandymanOutlinedIcon },
  { id: 'DVIR', type: 'sub-item', url: '/home/dvirs', icon: AssignmentOutlinedIcon },
  { id: 'DVIR Templates', type: 'sub-item', url: '/home/dvirtemplates', icon: () => <img src={DvirTemplate} alt='DVIR Templates' /> },
  // { id: 'Fuel Transactions', type: 'sub-item', url: '/home/fuel-transactions', icon: HandymanOutlinedIcon },
  { id: 'Safety', type: 'parent-item', url: '/home/safety', icon: LocalPoliceOutlinedIcon },
  { id: 'Driver Performance', type: 'sub-item', url: '/home/driver-performance', icon: AnalyticsOutlinedIcon },
  { id: 'Vehicle Performance', type: 'sub-item', url: '/home/vehicle-performance', icon: AnalyticsOutlinedIcon },
  { id: 'Safety Center', type: 'sub-item', url: '/home/safety-events', icon: ReportOutlinedIcon },
  { id: 'Coaching', type: 'sub-item', url: '/home/coaching', icon: SchoolOutlinedIcon },
  { id: 'Alerts', type: 'item', url: '/home/alerts', icon: CircleNotificationsOutlinedIcon },
  { id: 'Recordings', type: 'item', url: '/home/recordings', icon: VideocamOutlinedIcon },
  { id: 'Health', type: 'item', url: '/home/health', icon: MedicalServicesOutlinedIcon },
  { id: 'IFTA', type: 'item', url: '/home/ifta', icon: AssignmentOutlinedIcon },
  { id: 'Reports', type: 'item', url: '/home/reports', icon: BarChartIcon },
  // { id: 'VehicleActivity', type: 'item', url: '/home/vehicle-activity', icon: ReportOutlinedIcon },
  //  { id: 'div1', type: 'divider' },
  { id: 'Settings', type: 'item', url: '/home/settings', icon: SettingsIcon },
];
const MainLayout = Loadable(lazy(() => import('shared/src/components/layout/MainLayout')));
const Dashboard = Loadable(lazy(() => import('shared/src/portalPages/dashboard/Dashboard')));
const FleetMap = Loadable(lazy(() => import('./fleet/FleetMap')));
const FleetList = Loadable(lazy(() => import('./fleet/FleetList')));
const VehicleDetailsSidebar = Loadable(lazy(() => import('./fleet/VehicleDetailsSidebar')));
const VehicleTrips = Loadable(lazy(() => import('./fleet/VehicleTrips')));

const Alerts = Loadable(lazy(() => import('./alerts/Alerts')));
const AlertsMediaGallery = Loadable(lazy(() => import('./alerts/AlertsMediaGallery')));

const Assets = Loadable(lazy(() => import('./assets/Assets')));
const Drivers = Loadable(lazy(() => import('./drivers/Drivers')));
const CheckIns = Loadable(lazy(() => import('./drivers/CheckIns')));
const Maintenance = Loadable(lazy(() => import('./maintenance/Maintenance')));
const FaultCodes = Loadable(lazy(() => import('./faultcodes/FaultCodes')));
const Dvirs = Loadable(lazy(() => import('./dvir/Dvirs')));
const DvirTemplates = Loadable(lazy(() => import('./dvir/DvirTemplates')));
const Settings = Loadable(lazy(() => import('shared/src/portalPages/settings/Settings')));
const Trips = Loadable(lazy(() => import('./trips/Trips')));
const Safety = Loadable(lazy(() => import('shared/src/portalPages/dashboard/SafetyDashboard')));
const DriverPerformance = Loadable(lazy(() => import('./driverPerformance/DriverPerformance')));
const DriverPerformanceMobilePage = Loadable(lazy(() => import('./driverPerformance/DriverPerformanceMobilePage')));
const VehiclePerformance = Loadable(lazy(() => import('./vehiclePerformance/VehiclePerformance')));
const VehiclePerformanceMobilePage = Loadable(lazy(() => import('./vehiclePerformance/VehiclePerformanceMobilePage')));
const SafetyEvents = Loadable(lazy(() => import('./safetyEvents/SafetyEvents')));
const SafetyCoaching = Loadable(lazy(() => import('./safetyEvents/SafetyCoaching')));
const SafetyCoachingMobilePage = Loadable(lazy(() => import('./safetyEvents/SafetyCoachingMobilePage')));
const Recordings = Loadable(lazy(() => import('./recordings/Recordings')));
const Health = Loadable(lazy(() => import('shared/src/portalPages/health/Health')));
const HealthMobilePage = Loadable(lazy(() => import('shared/src/portalPages/health/HealthMobilePage')));
const Reports = Loadable(lazy(() => import('./reports/Reports')));
const VehicleActivity = Loadable(lazy(() => import('./vehicleActivity/VehicleActivity')));
const VehicleActivityMobilePage = Loadable(lazy(() => import('./vehicleActivity/VehicleActivityMobilePage')));
const IdlingEvent = Loadable(lazy(() => import('./idlingEvents/IdlingEvents')));
const VehicleIdlingEvents = Loadable(lazy(() => import('./idlingEvents/VehicleIdlingEvents')));
const GeoFence = Loadable(lazy(() => import('./geoFence/GeoFence')));
const GeoFenceMobile = Loadable(lazy(() => import('./geoFence/GeoFenceMobile')));
const GeoFenceVehicleReport = Loadable(lazy(() => import('./geoFence/GeoFenceVehicleReport')));

const ResponsiveComponent = Loadable(lazy(() => import('shared/src/components/responsive/ResponsiveComponent')));
const RecordingMobilePlayer = Loadable(lazy(() => import('./recordings/RecordingMobilePlayer')));
const IFTAReport = Loadable(lazy(() => import('./fuel/IFTAReport')));

const InstallerLayout = Loadable(lazy(() => import('./installer/InstallerLayout')));
const InstallerOrgSelect = Loadable(lazy(() => import('./installer/InstallerOrgSelect')));
const InstallWizard = Loadable(lazy(() => import('./installer/InstallWizard')));
const UpcomingScheduledMaintenance = Loadable(lazy(() => import('./maintenance/UpcomingScheduledMaintenance')));
const OverdueScheduledMaintenance = Loadable(lazy(() => import('./maintenance/OverdueScheduledMaintenance')));
const InstallationReport = Loadable(lazy(() => import('./deviceInstallation/InstallationReport')));
const CoachingReview = Loadable(lazy(() => import('./safetyEvents/SafetyCoachingReviewMobilePage')));
const CoachingSuccess = Loadable(lazy(() => import('./safetyEvents/SafetyCoachingSuccessMobilePage')));

function isEnabled(flags, flagKey) {
  if (!flagKey) return true;
  if (!flags) return true;
  return Object.prototype.hasOwnProperty.call(flags, flagKey) ? !!flags[flagKey] : true;
}

// Map menu item id -> feature flag name
const menuAccessMap = {
  DVIR: 'dvir',
  'DVIR Templates': 'dvir',
  IFTA: 'ifta',
};

function MenuLayout() {
  const { entityId, featureFlags = {} } = useOrgType();

  // Avoid flicker while org/flags load right after login
  if (!entityId) return null;

  // Keep order; first item (Dashboard) remains first
  const filteredMenu = portalMenuItems.filter((item) => {
    const requiredFlag = menuAccessMap[item.id];
    return isEnabled(featureFlags, requiredFlag);
  });

  return <MainLayout portalProps={{ name: 'app', menuItems: filteredMenu }} />;
}

const PortalRoutes = [
  {
    path: '/home',
    // element: <MainLayout portalProps={{ name: 'app', menuItems: portalMenuItems }} />,
    element: <MenuLayout />,
    errorElement: <ErrorPage />,
    loader: ({ request }) => protectedLoader({ request, store: GlobalStore }),
    children: [
      { index: true, element: <Dashboard type='app' /> },
      {
        path: 'fleetmap',
        element: <FleetMap />,
        children: [
          { index: true, element: <FleetList /> },
          { path: 'vehicle-details/:vehicleId', element: <VehicleDetailsSidebar /> },
          { path: 'vehicle-trips/:vehicleId/:tripId?', element: <VehicleTrips /> },
        ],
      },
      {
        path: 'vehicles',
        element: <Assets title='Vehicles' entityName='Vehicles' />,
      },
      {
        path: 'trips',
        element: <Trips type='trips' title='Trips' entityName='Trip' />,
      },
      {
        path: 'geofences',
        element: <ResponsiveComponent type='geofences' title='Geofences' entityName='Geofence' desktop={GeoFence} mobile={GeoFenceMobile} />,
      },
      {
        path: 'drivers',
        element: <Drivers type='User-Driver' title='Drivers' entityName='Driver' />,
      },
      {
        path: 'driver-checkins',
        element: <CheckIns />,
      },
      {
        path: 'maintenance/:vehicleImei?',
        element: <Maintenance type='maintenance' title='Maintenance' entityName='Maintenance' />,
      },
      {
        path: 'faultcodes/:vehicleImei?',
        element: <FaultCodes />,
      },
      {
        path: 'dvirs',
        element: (
          <FeatureGate flag='dvir'>
            <Dvirs type='dvir' title='DVIR' entityName='Dvir' />
          </FeatureGate>
        ),
      },
      {
        path: 'dvirtemplates',
        element: (
          <FeatureGate flag='dvir'>
            <DvirTemplates type='dvir' title='DVIR Templates' entityName='DvirTemplates' />
          </FeatureGate>
        ),
      },
      { path: 'ifta', element: <IFTAReport /> },
      {
        path: 'installation',
        element: (
          <ResponsiveComponent
            title='Installations'
            entityName='InstalledDevices'
            desktop={InstallationReport}
            mobile={InstallationReportMobilePage}
          />
        ),
      },
      { path: 'settings', element: <Settings type='app' /> },
      { path: 'safety', element: <Safety /> },
      { path: 'alerts', element: <Alerts /> },
      { path: 'alerts/:vehicleImei/:eventType/:logEntityId/:unixtime', element: <AlertsMediaGallery /> },
      {
        path: 'driver-performance',
        element: (
          <ResponsiveComponent
            type='app'
            title='Driver Performance Report'
            entityName='DriverPerformance'
            desktop={DriverPerformance}
            mobile={DriverPerformanceMobilePage}
          />
        ),
      },
      {
        path: 'vehicle-performance',
        element: (
          <ResponsiveComponent
            type='vehicle-performance'
            title='Vehicle Performance Report'
            entityName='VehiclePerformance'
            desktop={VehiclePerformance}
            mobile={VehiclePerformanceMobilePage}
          />
        ),
      },
      {
        path: 'safety-events',
        element: <SafetyEvents type='safety-events' title='Safety Center' entityName='SafetyEvents' />,
      },
      {
        path: 'coaching',
        element: (
          <ResponsiveComponent type='coaching' title='Coaching' entityName='Coaching' desktop={SafetyCoaching} mobile={SafetyCoachingMobilePage} />
        ),
      }, //<SafetyCoaching type='coaching' title='Coaching' entityName='Coaching' desktop={SafetyCoaching} />
      { path: 'recordings', element: <Recordings type='recordings' title='Recordings' entityName='Recording' /> },
      { path: 'recordingsMobilePlayer', element: <RecordingMobilePlayer /> },
      {
        path: 'health',
        element: <ResponsiveComponent type='health' title='Health' entityName='Health' desktop={Health} mobile={HealthMobilePage} />,
      },
      { path: 'reports', element: <Reports title='Reports' /> },
      {
        path: 'vehicle-activity',
        element: (
          <ResponsiveComponent
            title='Vehicle Activity Report'
            entityName='VehicleActivity'
            desktop={VehicleActivity}
            mobile={VehicleActivityMobilePage}
          />
        ),
      },
      {
        path: 'idling-events',
        element: <IdlingEvent title='Idling Event Report' entityName='IdlingEvent' />,
      },
      {
        path: 'vehicle-idling-events',
        element: <VehicleIdlingEvents title='Vehicle Idling Event Report' entityName='VehicleIdlingEvent' />,
      },
      {
        path: 'geofence-vehicle-report',
        element: <GeoFenceVehicleReport title='GeoFence Vehicle Report' entityName='GeoFenceVehicleReport' />,
      },
      {
        path: 'upcoming-schedule-maintenance',
        element: <UpcomingScheduledMaintenance title='Upcoming Scheduled Maintenance' entityName='UpcomingScheduledMaintenance' />,
      },
      {
        path: 'overdue-schedule-maintenance',
        element: <OverdueScheduledMaintenance title='Overdue Scheduled Maintenance' entityName='OverdueScheduledMaintenance' />,
      },
      {
        path: 'coaching-review',
        element: <ResponsiveComponent title='Coaching Review' entityName='CoachingReview' mobile={CoachingReview} />,
      },
      {
        path: 'coaching-success',
        element: <ResponsiveComponent title='Coaching Success' entityName='CoachingSuccess' mobile={CoachingSuccess} />,
      },
    ],
  },
  {
    path: '/install',
    element: <InstallerLayout />,
    errorElement: <ErrorPage />,
    loader: ({ request }) => protectedLoader({ request, store: GlobalStore }),
    children: [
      { index: true, element: <InstallerOrgSelect /> },
      { path: 'wizard', element: <InstallWizard /> },
    ],
  },
];

export { PortalRoutes };
